import {
    Add,
    CheckCircleOutline,
    CloseOutlined,
    ContentCopyOutlined,
    DeleteOutline,
    EditOutlined,
    MoreVert,
    ShieldOutlined,
    UndoOutlined,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useNavigate, useParams} from 'react-router-dom';
import {
    useDeleteRoleMutation,
    useGetPermissionCatalogQuery,
    useGetRoleQuery,
    useReplaceRolePermissionsMutation,
} from 'src/api';
import AMPModal from 'src/components/common/AMPModal';
import {CancelButton} from 'src/components/common/CancelButton';
import {ConformationModal} from 'src/components/common/ConformationModal';
import {PageHeader} from 'src/components/common/PageHeader';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {PermissionPickerModal} from 'src/components/iam/PermissionPickerModal';
import {RoleFormModal} from 'src/components/iam/RoleFormModal';
import {
    IAM_TONE,
    calloutSx,
    listRowSx,
    pillSx,
    stickyBarSx,
    subjectBadgeSx,
    subjectBandSx,
} from 'src/components/iam/iam.sx';
import {KpiCard} from 'src/pages/EnergyConsumptionReport/components/KpiCard';
import {useCan} from 'src/permissions/hooks';
import {PermissionDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import theme from 'src/theme';
import {cardSx} from '../AccessControl/AccessControl.sx';

/**
 * One role: what it grants, and how to change it.
 *
 * The resting state lists only what the role actually grants, grouped by subject. The screen this
 * replaces rendered all 148 catalog entries as checkboxes, which made "granted" and "available" the
 * same object on screen — there was no way to see what a role had, and no verb anywhere for adding or
 * removing. Everything not granted now lives behind an explicit Add, where a tick can only mean one
 * thing, and every granted row carries its own remove.
 *
 * Removals are staged rather than applied: the row stays, struck through, until Save. A change you
 * cannot see before committing it is a change you cannot check.
 */
export const RoleAccess: FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const params = useParams();
    const roleId = Number(params.roleId);

    const canManage = useCan('accessControl.manageRoles');
    const {data: role} = useGetRoleQuery(roleId, {skip: !roleId});
    const {data: catalog} = useGetPermissionCatalogQuery();
    const [replacePermissions, {isLoading: saving}] = useReplaceRolePermissionsMutation();
    const [deleteRole] = useDeleteRoleMutation();

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [editing, setEditing] = useState(false);
    const [cloning, setCloning] = useState(false);
    const [picking, setPicking] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const granted = useMemo(
        () => new Set((role?.permissions ?? []).map((permission) => permission.name)),
        [role]
    );

    /**
     * Seeded while rendering, not afterwards.
     *
     * An effect runs after the paint it belongs to, so the first frame had every permission granted
     * and none selected - which is exactly the shape of "staged for removal", and the page opened with
     * the whole list struck through in red before correcting itself. Setting state during render is
     * the supported way to say "this state follows that data": React re-renders before showing
     * anything, so there is no frame in between to see.
     */
    const [seededFrom, setSeededFrom] = useState<Set<string> | null>(null);
    if (granted !== seededFrom) {
        setSeededFrom(granted);
        setSelected(new Set(granted));
    }

    const added = useMemo(
        () => Array.from(selected).filter((name) => !granted.has(name)).length,
        [selected, granted]
    );
    const removed = useMemo(
        () => Array.from(granted).filter((name) => !selected.has(name)).length,
        [selected, granted]
    );
    const dirty = added > 0 || removed > 0;

    // Whether this caller may edit is the server's decision, arriving on the role itself. Working it
    // out again here would be a second implementation of an authorization rule.
    const readOnly = !canManage || !role?.editable;

    /**
     * What the role grants, plus anything staged for removal so it stays visible until saved.
     *
     * The role's own permissions come first and the catalog only fills in what was just picked. The
     * catalog is what this viewer may *grant*, which is not the same set as what the role already
     * holds: the platform can put something past a business's boundary into one of its roles, and
     * sourcing this list from the catalog alone would silently drop it from the page that exists to
     * show what the role does.
     */
    const shown = useMemo(() => {
        const names = new Set([
            ...Array.from(granted),
            ...Array.from(selected),
        ]);
        const known = new Map<string, PermissionDTO>();
        (catalog ?? []).forEach((permission) => known.set(permission.name, permission));
        (role?.permissions ?? []).forEach((permission) => known.set(permission.name, permission));

        const groups = new Map<string, PermissionDTO[]>();
        Array.from(known.values())
            .filter((permission) => names.has(permission.name))
            .forEach((permission) => {
                const list = groups.get(permission.subject) ?? [];
                list.push(permission);
                groups.set(permission.subject, list);
            });
        groups.forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
        return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [catalog, role, granted, selected]);

    const toggle = (name: string) => {
        const next = new Set(selected);
        if (!next.delete(name)) {
            next.add(name);
        }
        setSelected(next);
    };

    /**
     * Take a whole subject out, or put it back.
     *
     * Roles are edited a subject at a time far more often than a permission at a time — "this role has
     * no business touching invoices" is one decision, not six clicks that each need checking.
     *
     * It used to skip anything the role already granted, which left it able to un-tick a fresh pick and
     * nothing else — the one thing it exists for, taking a held subject away, was the case it refused.
     * That filter dated from bundles, where a permission arriving through a bundle genuinely could not
     * be removed row by row; bundles are gone, so every row in the group is now the same kind of thing.
     *
     * Removing drops a fresh pick from the page outright rather than striking it through, because
     * there is no grant behind it to strike: same as un-ticking that row on its own.
     */
    const toggleWholeSubject = (group: PermissionDTO[]) => {
        const removing = group.some((permission) => selected.has(permission.name));
        const next = new Set(selected);
        group.forEach((permission) =>
            removing ? next.delete(permission.name) : next.add(permission.name)
        );
        setSelected(next);
    };

    const notify = (error: unknown) =>
        NotifyError(
            isErrorObject(error)
                ? error.data.message
                : intl.formatMessage({id: 'somethingWentWrong', defaultMessage: 'Something went wrong'})
        );

    /**
     * Writes a set of permissions, and answers whether it landed.
     *
     * Takes the set rather than reading `selected`, because the picker commits the moment it is
     * confirmed and its additions have not reached state yet - React batches, so reading `selected`
     * here would save the set as it was before the picks.
     */
    const persist = async (names: Set<string>) => {
        try {
            const {payload: changed} = await replacePermissions({
                roleId,
                data: {permissionNames: Array.from(names)},
            }).unwrap();
            NotifySuccess(intl.formatMessage({id: 'saved', defaultMessage: 'Saved'}));
            // Saving a shared starting role changes this business's own copy of it. Staying on the
            // shared one made the save look lost - its grants never moved - so follow the copy.
            if (changed && changed !== roleId) {
                navigate(
                    AppRouteSpace([
                        AppRouteEnum.app,
                        AppRouteEnum.accessControl,
                        AppRouteEnum.roles,
                        String(changed),
                    ]),
                    {replace: true}
                );
            }
            return true;
        } catch (error) {
            notify(error);
            return false;
        }
    };

    const handleSave = () => persist(selected);

    const handleDelete = async () => {
        try {
            await deleteRole(roleId).unwrap();
            NotifySuccess(intl.formatMessage({id: 'deleted', defaultMessage: 'Deleted'}));
            navigate(AppRouteSpace([AppRouteEnum.app, AppRouteEnum.accessControl]) + '?section=roles');
        } catch (error) {
            notify(error);
        } finally {
            setConfirmingDelete(false);
        }
    };

    if (!role) {
        return null;
    }

    return (
        <Box sx={{pb: 12}}>
            <PageHeader
                title={role.name}
                icon={<ShieldOutlined/>}
                // One action, whichever applies: add to a role you may edit, copy one you may not.
                // The paragraph that used to explain the second case belonged to the system-role
                // split and went with it — every role is a business's role now.
                onlyTitle={!canManage}
                buttonText={role.editable ? 'accessControlAddPermissions' : 'accessControlCloneRole'}
                onClick={role.editable ? () => setPicking(true) : () => setCloning(true)}
                titleSuffix={
                    role.protectedRole ? (
                        <Typography sx={pillSx(IAM_TONE.protectedTone)}>
                            {intl.formatMessage({
                                id: 'accessControlProtectedRole',
                                defaultMessage: 'Protected',
                            })}
                        </Typography>
                    ) : role.builtIn ? (
                        <Typography sx={pillSx(IAM_TONE.platform)}>
                            {intl.formatMessage({
                                id: 'accessControlBuiltInRole',
                                defaultMessage: 'Built-in',
                            })}
                        </Typography>
                    ) : undefined
                }
            >
                {canManage && role.editable && (
                    <>
                        <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)}>
                            <MoreVert/>
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchor}
                            open={!!menuAnchor}
                            onClose={() => setMenuAnchor(null)}
                        >
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchor(null);
                                    setCloning(true);
                                }}
                            >
                                <ContentCopyOutlined fontSize="small" sx={{mr: 1}}/>
                                <FormattedMessage id="accessControlCloneRole" defaultMessage="Copy role"/>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchor(null);
                                    setEditing(true);
                                }}
                            >
                                <EditOutlined fontSize="small" sx={{mr: 1}}/>
                                <FormattedMessage id="edit" defaultMessage="Edit"/>
                            </MenuItem>
                            {/* A disabled MenuItem swallows pointer events, so the tooltip needs a live
                                element of its own to fire on. */}
                            <Tooltip
                                title={
                                    role.protectedRole
                                        ? intl.formatMessage({
                                            id: 'accessControlDeleteProtectedTooltip',
                                            defaultMessage: 'Protected roles cannot be deleted.',
                                        })
                                        : role.builtIn
                                            ? intl.formatMessage({
                                                id: 'accessControlDeleteBuiltInTooltip',
                                                defaultMessage:
                                                    'New users of this kind start with this role, so it cannot be deleted.',
                                            })
                                            : ''
                                }
                            >
                                <span>
                                    <MenuItem
                                        disabled={role.protectedRole || role.builtIn}
                                        onClick={() => {
                                            setMenuAnchor(null);
                                            setConfirmingDelete(true);
                                        }}
                                    >
                                        <DeleteOutline fontSize="small" sx={{mr: 1}}/>
                                        <FormattedMessage id="delete" defaultMessage="Delete"/>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        </Menu>
                    </>
                )}
            </PageHeader>

            {role.description && (
                <Typography
                    sx={{mt: 1, fontSize: '0.875rem', color: theme.palette.grey[500], maxWidth: 760}}
                >
                    {role.description}
                </Typography>
            )}

            <Grid container spacing={2} sx={{mt: 0.5}}>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlTabPermissions',
                            defaultMessage: 'Permissions',
                        })}
                        value={String(new Set([...Array.from(selected), ...Array.from(granted)]).size)}
                        unit={intl.formatMessage(
                            {id: 'accessControlOfTotal', defaultMessage: 'of {total}'},
                            {total: catalog?.length ?? 0}
                        )}
                        icon={<CheckCircleOutline fontSize="small"/>}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlHolders',
                            defaultMessage: 'Holders',
                        })}
                        value={String(role.holders ?? 0)}
                        unit={intl.formatMessage({
                            id: 'accessControlPeopleUnit',
                            defaultMessage: 'people',
                        })}
                        icon={<ShieldOutlined fontSize="small"/>}
                    />
                </Grid>
            </Grid>

            {!role.protectedRole && role.builtIn && (
                <Alert severity="info" sx={{mt: 2, borderRadius: '14px'}}>
                    <FormattedMessage
                        id="accessControlBuiltInRoleExplained"
                        defaultMessage="Every new user of this kind starts with this role, so the server looks it up by name and will not let it be deleted. Renaming it and changing what it grants are both fine."
                    />
                </Alert>
            )}

            {role.protectedRole && (
                <Alert severity="warning" sx={{mt: 2, borderRadius: '14px'}}>
                    <FormattedMessage
                        id="accessControlProtectedRoleExplained"
                        defaultMessage="Protected role. It cannot be deleted, and it cannot be left without a holder. This is the guard that keeps at least one administrator able to administer the system — you can add to it, but some removals will be refused."
                    />
                </Alert>
            )}

            {shown.length === 0 ? (
                <Box sx={{...calloutSx(IAM_TONE.none), textAlign: 'center', py: 4}}>
                    <Typography sx={{fontWeight: 600, mb: 0.5}}>
                        <FormattedMessage
                            id="accessControlRoleEmpty"
                            defaultMessage="This role grants nothing yet."
                        />
                    </Typography>
                    {/* No button here on purpose: the page header already carries Add permissions, and
                        two identical primary actions on one screen make the reader stop to work out
                        whether they differ. The hint names the one that exists. */}
                    <Typography sx={{fontSize: '0.875rem', color: theme.palette.grey[500]}}>
                        <FormattedMessage
                            id="accessControlRoleEmptyHint"
                            defaultMessage="Nobody holding it can do anything. Use Add permissions to start."
                        />
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={2} sx={{mt: 3}}>
                    {shown.map(([subject, group]) => (
                        <Box key={subject} sx={cardSx}>
                            <Box sx={subjectBandSx}>
                                <Box sx={subjectBadgeSx}>
                                    <CheckCircleOutline fontSize="small"/>
                                </Box>
                                <Typography sx={{fontWeight: 600, fontSize: '0.875rem'}}>
                                    {intl.formatMessage({
                                        id: `permissionSubject.${subject}`,
                                        defaultMessage: subject,
                                    })}
                                </Typography>
                                {/*
                                    Counts what the subject will grant once saved, not the union of now
                                    and then. The union could only ever grow, so striking a whole
                                    subject through left every row crossed out beside a badge still
                                    claiming six - the two halves of the same band disagreeing.
                                */}
                                <Typography sx={{...pillSx(IAM_TONE.granted), ml: 'auto'}}>
                                    {intl.formatMessage(
                                        {id: 'accessControlNGranted', defaultMessage: '{count} granted'},
                                        {
                                            count: group.filter((permission) =>
                                                selected.has(permission.name)
                                            ).length,
                                        }
                                    )}
                                </Typography>

                                {!readOnly && (
                                    <Tooltip
                                        title={intl.formatMessage(
                                            group.some((permission) => selected.has(permission.name))
                                                ? {
                                                    id: 'accessControlRemoveSubject',
                                                    defaultMessage: 'Remove all of these',
                                                }
                                                : {
                                                    id: 'accessControlUndoRemove',
                                                    defaultMessage: 'Undo',
                                                }
                                        )}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleWholeSubject(group)}
                                        >
                                            {group.some((permission) => selected.has(permission.name)) ? (
                                                <CloseOutlined fontSize="small"/>
                                            ) : (
                                                <UndoOutlined fontSize="small"/>
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {group.map((permission) => {
                                // Struck through where a grant is on its way out: held now, not in the
                                // set about to be saved.
                                const staged = granted.has(permission.name) && !selected.has(permission.name);
                                return (
                                    <Box key={permission.name} sx={listRowSx}>
                                        <CheckCircleOutline
                                            sx={{
                                                fontSize: 16,
                                                color: staged
                                                    ? theme.palette.neutral[400]
                                                    : IAM_TONE.granted.fg,
                                            }}
                                        />
                                        <Box sx={{minWidth: 0, flex: 1}}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        textDecoration: staged ? 'line-through' : 'none',
                                                        color: staged ? IAM_TONE.refused.fg : 'inherit',
                                                    }}
                                                >
                                                    {intl.formatMessage({
                                                        id: `permission.${permission.subject}.${permission.action}`,
                                                        defaultMessage: permission.name,
                                                    })}
                                                </Typography>
                                            </Stack>
                                        </Box>

                                        {!readOnly && (
                                            <Tooltip
                                                title={intl.formatMessage(
                                                    staged
                                                        ? {id: 'accessControlUndoRemove', defaultMessage: 'Undo'}
                                                        : {
                                                            id: 'accessControlRemovePermission',
                                                            defaultMessage: 'Remove from this role',
                                                        }
                                                )}
                                            >
                                                <IconButton
                                                    className="rowAction"
                                                    size="small"
                                                    onClick={() => toggle(permission.name)}
                                                >
                                                    {staged ? (
                                                        <UndoOutlined fontSize="small"/>
                                                    ) : (
                                                        <CloseOutlined fontSize="small"/>
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                );
                            })}

                            {!readOnly && (
                                <Box sx={{px: 2.5, py: 1.25}}>
                                    <Button
                                        size="small"
                                        startIcon={<Add/>}
                                        sx={{textTransform: 'none'}}
                                        onClick={() => setPicking(true)}
                                    >
                                        <FormattedMessage id="add" defaultMessage="Add"/>
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ))}
                </Stack>
            )}

            {dirty && (
                <Box sx={stickyBarSx}>
                    {added > 0 && (
                        <Typography sx={pillSx(IAM_TONE.granted)}>
                            {intl.formatMessage(
                                {id: 'accessControlPendingAdded', defaultMessage: '+{count} added'},
                                {count: added}
                            )}
                        </Typography>
                    )}
                    {removed > 0 && (
                        <Typography sx={pillSx(IAM_TONE.refused)}>
                            {intl.formatMessage(
                                {id: 'accessControlPendingRemoved', defaultMessage: '-{count} removed'},
                                {count: removed}
                            )}
                        </Typography>
                    )}
                    <Stack direction="row" spacing={1.5} sx={{ml: 'auto'}}>
                        <CancelButton
                            text="accessControlDiscardChanges"
                            handler={() => setSelected(new Set(granted))}
                        />
                        <SubmitButton
                            text="accessControlSaveChanges"
                            color="brand"
                            isSubmitting={saving}
                            handler={handleSave}
                        />
                    </Stack>
                </Box>
            )}

            {picking && (
                <PermissionPickerModal
                    catalog={catalog ?? []}
                    held={selected}
                    title={intl.formatMessage({
                        id: 'accessControlAddToRole',
                        defaultMessage: 'Add to this role',
                    })}
                    onClose={() => setPicking(false)}
                    saving={saving}
                    onAdd={async (names) => {
                        // Saved, never staged. Putting the picks into `selected` first made the page
                        // dirty for the frame between the write and the refetch, and the footer bar
                        // appeared and vanished on its own - a control flashing past is read as
                        // something gone wrong. The refetched role is the only thing that moves the
                        // page, so there is no in-between state to see.
                        //
                        // The endpoint replaces the whole set, so this commits anything already struck
                        // through on the page as well - which is what "save changes" says, and the
                        // only reading a replace can honour.
                        const next = new Set([...Array.from(selected), ...names]);
                        if (await persist(next)) {
                            setPicking(false);
                        }
                        // Left open on failure: the ticks are still in the modal, so retrying is one
                        // click rather than a hunt back through thirty collapsed subjects.
                    }}
                />
            )}

            {editing && <RoleFormModal role={role} onClose={() => setEditing(false)}/>}
            {cloning && <RoleFormModal role={role} clone onClose={() => setCloning(false)}/>}

            <AMPModal
                open={confirmingDelete}
                handleClose={() => setConfirmingDelete(false)}
                title={intl.formatMessage({id: 'accessControlDeleteRole', defaultMessage: 'Delete role'})}
                type="sm"
                fullWidth
            >
                <ConformationModal
                    text="accessControlDeleteRoleConfirm"
                    caption={role.name}
                    handleClose={() => setConfirmingDelete(false)}
                    handleSubmit={handleDelete}
                />
            </AMPModal>
        </Box>
    );
};
